const normalize = (value) =>
  String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toLowerCase();

/*
 * Kit builder: picks the kit variant from size, diet, duration and flavor.
 * Each diet (100% natural, Mix, Topper) has its own kit product and variant list.
 * Options are matched by position (Porte / Duração / Sabor) and the variant is
 * re-resolved in the capture phase of submit, before s-product-form reads the id,
 * so the cart always gets exactly the combination shown as selected.
 */
class SKitBuilder extends HTMLElement {
  #ac;
  #variantsByDiet = {};

  connectedCallback() {
    this.#ac = new AbortController();
    const signal = this.#ac.signal;
    this.querySelectorAll('[data-kit-variants]').forEach((payload) => {
      try {
        this.#variantsByDiet[payload.dataset.kitVariants || 'natural'] = JSON.parse(payload.textContent || '[]');
      } catch {
        this.#variantsByDiet[payload.dataset.kitVariants || 'natural'] = [];
      }
    });
    this.addEventListener('change', () => this.#sync(), { signal });
    this.addEventListener('input', () => this.#sync(), { signal });
    this.addEventListener(
      'submit',
      (event) => {
        const ok = this.#sync();
        if (!ok) {
          event.preventDefault();
          event.stopImmediatePropagation();
        }
      },
      { capture: true, signal }
    );
    // Browsers can restore radio states from history after scripts run.
    window.addEventListener('pageshow', () => this.#sync(), { signal });
    this.#sync();
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  #selected(name) {
    const input = this.querySelector(`input[name="${name}"]:checked`);
    return input ? input.value : '';
  }

  #sync() {
    const idInput = this.querySelector('[data-kit-id]');
    const button = this.querySelector('[data-kit-add]');
    if (!idInput || !button) return false;

    const diet = this.#selected('kit-diet') || 'natural';
    const variants = this.#variantsByDiet[diet];
    const wanted = [this.#selected('kit-size'), this.#selected('kit-days'), this.#selected('kit-flavor')].map(normalize);
    const match =
      variants && wanted.every(Boolean)
        ? variants.find((variant) => {
            const options = (variant.options || []).map(normalize);
            return options.length === wanted.length && wanted.every((value, index) => options[index] === value);
          })
        : undefined;

    const available = Boolean(match && match.available);
    idInput.value = available ? String(match.id) : '';
    button.disabled = !available;
    // The label itself tells the buyer why the button can't add (diet kit not live yet, or combination unavailable).
    if (available) button.textContent = button.dataset.addLabel;
    else button.textContent = variants ? button.dataset.unavailableLabel : button.dataset.soonLabel || button.dataset.unavailableLabel;
    return available;
  }
}

if (!customElements.get('s-kit-builder')) customElements.define('s-kit-builder', SKitBuilder);
